/**
 * Hours Entry Modal
 *
 * Modal for recording hours worked on an assignment.
 * Supports manual hours entry or check-in/check-out times.
 */

import { useState, useEffect } from 'react';
import { updateAssignmentHours } from '../../api/assignments';
import { getErrorMessage } from '../../api/client';
import { formatRate, formatPay } from '../../utils/rateFormatting';
import { effectivePay } from '../../utils/assignmentHours';
import type { RateType } from '../../types/models';
import Modal from '../ui/Modal';

interface HoursEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  assignmentId: string;
  crewName: string;
  roleName: string;
  effectiveRate: number | null | undefined;
  effectiveRateType: RateType | null | undefined;
  currentHours: string | number | null | undefined;
  currentCheckIn: string | null | undefined;
  currentCheckOut: string | null | undefined;
  /**
   * The event's crew-call-to-end span. When no hours have been entered this
   * is what every report already assumes, so the form starts from it.
   */
  scheduledHours?: number | null;
}

export default function HoursEntryModal({
  isOpen,
  onClose,
  onSaved,
  assignmentId,
  crewName,
  roleName,
  effectiveRate,
  effectiveRateType,
  currentHours,
  currentCheckIn,
  currentCheckOut,
  scheduledHours = null,
}: HoursEntryModalProps) {
  const [hours, setHours] = useState('');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [entryMode, setEntryMode] = useState<'hours' | 'times'>('hours');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form values when modal opens
  useEffect(() => {
    if (isOpen) {
      // Parse current hours
      if (currentHours !== null && currentHours !== undefined) {
        const hoursNum = typeof currentHours === 'string' ? parseFloat(currentHours) : currentHours;
        setHours(hoursNum.toString());
      } else if (scheduledHours !== null) {
        setHours(scheduledHours.toString());
      } else {
        setHours('');
      }

      // Parse check-in/out times (extract time portion from ISO string)
      if (currentCheckIn) {
        const time = new Date(currentCheckIn).toTimeString().slice(0, 5);
        setCheckInTime(time);
      } else {
        setCheckInTime('');
      }

      if (currentCheckOut) {
        const time = new Date(currentCheckOut).toTimeString().slice(0, 5);
        setCheckOutTime(time);
      } else {
        setCheckOutTime('');
      }

      setError(null);
    }
  }, [isOpen, currentHours, currentCheckIn, currentCheckOut, scheduledHours]);

  const hoursEntered = currentHours !== null && currentHours !== undefined;

  // Estimated pay — the same rule the reports use (utils/assignmentHours.ts).
  const hoursNum = parseFloat(hours);
  const estimatedPay = effectivePay(
    { effectiveRate, effectiveRateType },
    Number.isFinite(hoursNum) && hoursNum > 0 ? hoursNum : null
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const hoursNum = parseFloat(hours);
      if (isNaN(hoursNum) || hoursNum < 0) {
        setError('Please enter valid hours');
        setIsSubmitting(false);
        return;
      }

      // Build payload
      const payload: {
        hoursWorked?: number | null;
        checkInTime?: string | null;
        checkOutTime?: string | null;
      } = {
        hoursWorked: hoursNum > 0 ? hoursNum : null,
      };

      // Include times if in times mode and values are set
      if (entryMode === 'times') {
        if (checkInTime) {
          // Create ISO string for today with the given time
          const today = new Date().toISOString().split('T')[0];
          payload.checkInTime = `${today}T${checkInTime}:00.000Z`;
        }
        if (checkOutTime) {
          const today = new Date().toISOString().split('T')[0];
          payload.checkOutTime = `${today}T${checkOutTime}:00.000Z`;
        }
      }

      await updateAssignmentHours(assignmentId, payload);
      onSaved();
      handleClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setHours('');
    setCheckInTime('');
    setCheckOutTime('');
    setEntryMode('hours');
    setError(null);
    onClose();
  };

  // Calculate hours from times when in times mode
  const calculateHoursFromTimes = () => {
    if (!checkInTime || !checkOutTime) return;

    const [inHours, inMinutes] = checkInTime.split(':').map(Number);
    const [outHours, outMinutes] = checkOutTime.split(':').map(Number);

    const inTotalMinutes = inHours * 60 + inMinutes;
    const outTotalMinutes = outHours * 60 + outMinutes;

    let diffMinutes = outTotalMinutes - inTotalMinutes;
    if (diffMinutes < 0) {
      // Assume next day (overnight shift)
      diffMinutes += 24 * 60;
    }

    const calculatedHours = diffMinutes / 60;
    setHours(calculatedHours.toFixed(2));
  };

  useEffect(() => {
    if (entryMode === 'times') {
      calculateHoursFromTimes();
    }
  }, [checkInTime, checkOutTime, entryMode]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" labelledBy="hours-modal-title">
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 id="hours-modal-title" className="text-lg font-semibold text-gray-900">Record Hours</h3>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {crewName} - {roleName}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Rate info */}
          {effectiveRate && (
            <div className="p-3 bg-gray-50 rounded-md">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Rate:</span>{' '}
                {formatRate(effectiveRate, effectiveRateType)}
              </div>
            </div>
          )}

          {/* Entry mode toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEntryMode('hours')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                entryMode === 'hours'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Manual Hours
            </button>
            <button
              type="button"
              onClick={() => setEntryMode('times')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                entryMode === 'times'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Check In/Out
            </button>
          </div>

          {entryMode === 'hours' ? (
            /* Manual hours entry */
            <div>
              <label htmlFor="hours" className="block text-sm font-medium text-gray-700 mb-1">
                Hours Worked
              </label>
              <input
                type="number"
                id="hours"
                step="0.01"
                min="0"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                placeholder="8.0"
              />
              {!hoursEntered && scheduledHours !== null && (
                <p className="mt-1 text-xs text-gray-500">
                  Prefilled from the schedule (crew call to end time, {scheduledHours} hrs). Reports
                  already assume this until you save actual hours.
                </p>
              )}
            </div>
          ) : (
            /* Time entry */
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="checkInTime" className="block text-sm font-medium text-gray-700 mb-1">
                  Check In
                </label>
                <input
                  type="time"
                  id="checkInTime"
                  value={checkInTime}
                  onChange={(e) => setCheckInTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                />
              </div>
              <div>
                <label htmlFor="checkOutTime" className="block text-sm font-medium text-gray-700 mb-1">
                  Check Out
                </label>
                <input
                  type="time"
                  id="checkOutTime"
                  value={checkOutTime}
                  onChange={(e) => setCheckOutTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                />
              </div>
              {hours && (
                <div className="col-span-2 text-sm text-gray-600">
                  Calculated: <span className="font-medium">{hours} hours</span>
                </div>
              )}
            </div>
          )}

          {/* Estimated pay */}
          {estimatedPay !== null && (
            <div className="p-3 bg-green-50 rounded-md border border-green-200">
              <div className="text-sm">
                <span className="text-green-700">Estimated Pay:</span>{' '}
                <span className="font-semibold text-green-800">{formatPay(estimatedPay)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !hours}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Hours'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
